import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Download, Apple, Play } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

const TruckerEnglish = () => {
  const { language } = useLanguage();
  const isRu = language === 'ru';

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title={isRu ? 'Трак драйвер: Специальное Предложение' : "Truck Driver: Special Offer"}
        description={isRu 
          ? 'Новый указ президента Трампа требует от водителей грузовиков знания английского языка для соблюдения правил.'
          : 'The new decree by President Trump requires truck drivers to know English to comply with regulations.'}
        canonical="/english"
      />
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-12 lg:pt-48 lg:pb-16 overflow-hidden bg-navy-950">
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-8 items-stretch max-w-6xl mx-auto">
              {/* Left Side: Truck Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[240px] md:h-[500px] lg:h-[600px] group">
                <img 
                  src="https://vibe.filesafe.space/1777392978847194987/assets/1b493395-436e-4d95-811f-bf4ff59fb7af.png" 
                  alt="Truck" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-navy-950/20 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 lg:right-10">
                  <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-4 border border-primary/30 tracking-wide uppercase backdrop-blur-md shadow-lg">
                    {isRu ? 'Специальное Предложение' : 'Special Offer'}
                  </div>
                </div>
              </div>

              {/* Right Side: Uploaded Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[240px] md:h-[500px] lg:h-[600px] bg-black">
                <img 
                  src="https://vibe.filesafe.space/1777392978847194987/attachments/ce0c2eeb-973b-4f70-922f-c08d559c7a4a.png" 
                  alt="Trucker's English by AITIM Group" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-24 bg-white relative">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-5xl font-black text-navy-950 mb-8">
                {isRu ? 'Трак драйвер:' : 'Truck Driver:'}
              </h2>
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed mb-6 font-medium">
                {isRu ? (
                  <>Новый указ президента <span className="font-bold text-primary">Трампа</span> требует от водителей грузовиков знания английского языка для соблюдения правил. Несоответствие этому стандарту может привести к крупным штрафам для вас и вашей компании — <span className="font-bold text-red-500">Out-of-Service</span> или увольнениям.</>
                ) : (
                  <>The new decree by President <span className="font-bold text-primary">Trump</span> requires truck drivers to know English to comply with regulations. Failure to meet this standard can result in hefty fines for you and your company — <span className="font-bold text-red-500">Out-of-Service</span> or termination.</>
                )}
              </p>
              
              <div className="py-8">
                <p className="text-xl md:text-2xl font-bold text-navy-950 mb-4">
                  {isRu ? 'Не рискуйте большими штрафами' : 'Do not risk large fines'}
                </p>
                <p className="text-xl md:text-2xl font-bold text-navy-950 mb-8">
                  {isRu ? 'или приостановкой работы!' : 'or work suspension!'}
                </p>
                <p className="text-2xl md:text-3xl font-black text-primary mb-8 bg-primary/10 inline-block px-6 py-4 rounded-2xl">
                  {isRu ? 'Oсвойте базовый английский необходимый при общении с офицером прямо сейчас!' : 'Master the basic English needed to communicate with an officer right now!'}
                </p>
                <p className="text-lg md:text-xl text-slate-700 font-medium mb-12">
                  {isRu ? 'Не позволяйте языковому барьеру остановить вашу карьеру!' : 'Do not let the language barrier stop your career!'}
                </p>
              </div>

              <div className="mb-8">
                <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6">
                  {isRu ? 'Скачать здесь:' : 'Download here:'}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a href="https://apps.apple.com/us/app/aitimgroup/id6745021005" target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-navy-950 hover:bg-navy-900 text-white px-8 py-4 rounded-xl transition-transform hover:-translate-y-1 w-full sm:w-auto justify-center">
                    <Apple className="w-8 h-8" />
                    <div className="text-left">
                      <div className="text-[10px] leading-tight opacity-80">Download on the</div>
                      <div className="text-lg font-semibold leading-tight">App Store</div>
                    </div>
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.aitimgroup.wl&hl=en" target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-navy-950 hover:bg-navy-900 text-white px-8 py-4 rounded-xl transition-transform hover:-translate-y-1 w-full sm:w-auto justify-center">
                    <Play className="w-8 h-8 fill-current" />
                    <div className="text-left">
                      <div className="text-[10px] leading-tight opacity-80">GET IT ON</div>
                      <div className="text-lg font-semibold leading-tight">Google Play</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-24 bg-slate-50 border-t border-slate-200">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-black text-navy-950">
                  {isRu ? 'Заполните форму и начнём!' : 'Fill out the form and let\'s start!'}
                </h2>
              </div>

              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
                <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">{isRu ? 'Имя *' : 'First Name *'}</Label>
                      <Input id="firstName" placeholder={isRu ? 'Имя' : 'First Name'} className="h-12 bg-slate-50" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">{isRu ? 'Фамилия *' : 'Last Name *'}</Label>
                      <Input id="lastName" placeholder={isRu ? 'Фамилия' : 'Last Name'} className="h-12 bg-slate-50" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">{isRu ? 'Телефон *' : 'Phone *'}</Label>
                      <Input id="phone" type="tel" placeholder={isRu ? 'Номер телефона' : 'Phone Number'} className="h-12 bg-slate-50" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{isRu ? 'Email *' : 'Email *'}</Label>
                      <Input id="email" type="email" placeholder={isRu ? 'Адрес электронной почты' : 'Email Address'} className="h-12 bg-slate-50" required />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">{isRu ? 'Адрес *' : 'Address *'}</Label>
                    <Input id="address" placeholder={isRu ? 'Улица, дом' : 'Street Address'} className="h-12 bg-slate-50 mb-4" required />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input placeholder={isRu ? 'Город' : 'City'} className="h-12 bg-slate-50" />
                      <Input placeholder={isRu ? 'Штат' : 'State'} className="h-12 bg-slate-50" />
                      <Input placeholder={isRu ? 'Страна' : 'Country'} className="h-12 bg-slate-50" />
                      <Input placeholder={isRu ? 'Почтовый индекс' : 'Postal code'} className="h-12 bg-slate-50" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="company">{isRu ? 'Название компании' : 'Company Name'}</Label>
                      <Input id="company" placeholder={isRu ? 'Организация' : 'Organization'} className="h-12 bg-slate-50" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="title">{isRu ? 'Ваша должность' : 'Your Title'}</Label>
                      <Select>
                        <SelectTrigger className="h-12 bg-slate-50">
                          <SelectValue placeholder={isRu ? 'Выберите должность' : 'Select Title'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="driver">{isRu ? 'Водитель' : 'Driver'}</SelectItem>
                          <SelectItem value="owner">{isRu ? 'Владелец (Owner Operator)' : 'Owner Operator'}</SelectItem>
                          <SelectItem value="fleet">{isRu ? 'Менеджер автопарка' : 'Fleet Manager'}</SelectItem>
                          <SelectItem value="other">{isRu ? 'Другое' : 'Other'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-6 bg-slate-50 p-6 rounded-2xl border border-slate-100">
                    <div className="flex items-start space-x-3">
                      <Checkbox id="consent-1" className="mt-1" />
                      <Label htmlFor="consent-1" className="text-sm text-slate-600 leading-relaxed font-normal cursor-pointer">
                        {isRu ? (
                          <>Устанавливая этот флажок, я соглашаюсь получать <span className="font-bold">звонки и текстовые сообщения</span> от <span className="font-bold">AITIM GROUP</span> на указанный мной номер. Сообщения <span className="font-bold">могут отправляться автоматически</span>. Частота сообщений варьируется. Могут применяться тарифы на передачу данных. Ответьте <span className="font-bold">STOP</span>, чтобы прекратить получение сообщений, или напишите на <a href="mailto:info@aitimgroup.com" className="font-bold text-primary hover:underline">info@aitimgroup.com</a>.</>
                        ) : (
                          <>By checking this box, I consent to receive <span className="font-bold">calls and text messages</span> from <span className="font-bold">AITIM GROUP</span> at the number I provide about my account, orders, or services I requested (e.g., reminders, confirmations, notifications). These communications <span className="font-bold">may be sent automatically and may use a pre-recorded or AI-generated voice</span>. Message frequency varies. Message & data rates may apply. Reply <span className="font-bold">STOP</span> to end texts. For calls, say <span className="font-bold">&quot;do not call&quot;</span> or press the opt-out key; or email <a href="mailto:info@aitimgroup.com" className="font-bold text-primary hover:underline">info@aitimgroup.com</a> to opt out.</>
                        )}
                      </Label>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Checkbox id="consent-2" className="mt-1" />
                      <Label htmlFor="consent-2" className="text-sm text-slate-600 leading-relaxed font-normal cursor-pointer">
                        {isRu ? (
                          <>Устанавливая этот флажок, я соглашаюсь получать <span className="font-bold">маркетинговые звонки и текстовые сообщения</span> от <span className="font-bold">AITIM GROUP</span> на указанный мной номер (например, специальные предложения, скидки, обновления). Сообщения <span className="font-bold">могут отправляться автоматически</span>. Частота сообщений варьируется. Могут применяться тарифы на передачу данных. <span className="font-bold">Согласие не является условием покупки.</span> Ответьте <span className="font-bold">STOP</span>, чтобы прекратить получение сообщений, или напишите на <a href="mailto:info@aitimgroup.com" className="font-bold text-primary hover:underline">info@aitimgroup.com</a>.</>
                        ) : (
                          <>By checking this box, I agree to receive <span className="font-bold">marketing calls and text messages</span> from <span className="font-bold">AITIM GROUP</span> at the number I provide (e.g., special offers, discounts, product updates). Calls/texts <span className="font-bold">may be sent automatically and may use a pre-recorded or AI-generated voice</span>. Message frequency varies. Message & data rates may apply. <span className="font-bold">Consent is not a condition of purchase.</span> Reply <span className="font-bold">STOP</span> to end texts. For calls, say <span className="font-bold">&quot;do not call&quot;</span> or press the opt-out key; or email <a href="mailto:info@aitimgroup.com" className="font-bold text-primary hover:underline">info@aitimgroup.com</a> to opt out.</>
                        )}
                      </Label>
                    </div>
                  </div>

                  <Link to="/free-consultation">
                    <Button type="button" className="w-full h-14 text-lg font-bold bg-navy-950 hover:bg-navy-900 text-white rounded-xl">
                      {isRu ? 'Связаться с нами' : 'Get In Touch'}
                    </Button>
                  </Link>
                  
                  <div className="text-center text-xs text-slate-400 mt-4">
                    protected by <span className="font-bold">reCAPTCHA</span>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default TruckerEnglish;
